import { SkillData, SkillNode, SkillLink, SkillDocument, Viewport, SKILL_DATA_VERSION, EntitlementLevel, Entitlement, SkillDataState, Result, ok, fail, documentEntryHasLinkedGuide } from '@skill-trees/shared';
import { produce, produceWithPatches, applyPatches } from 'immer';
import { TreeDataPersistor, PersistorErrorType, LoadedDocument } from '../persistence/Persistor';
import { UndoRedoInteractor } from './UndoRedoInteractor';

/** Markdown persisted when an author attaches a new leaf guide (editor + skill panel). */
export const NEW_SKILL_LEAF_GUIDE_STARTER_MARKDOWN =
    '# New Skill Leaf Guide\n---\nNo guide written for this leaf yet.';

/**
 * Trailing debounce for draft auto-saves: a burst of mutations (typing, drags,
 * repeated undo) collapses into one save of the final state instead of a full-tree
 * POST per step.
 */
export const DRAFT_SAVE_DEBOUNCE_MS = 500;

interface PatchOptions {
    /**
     * Record the mutation on the undo stack (default true). UI-only writes such as
     * selection and viewport pass false: they are not document edits, so they neither
     * record an entry nor clear the redo stack.
     */
    undoable?: boolean;
    /**
     * Consecutive undoable patches with the same key merge into a single undo entry
     * (first entry's undo patches, latest redo patches) — e.g. a stream of position
     * updates within one drag gesture. Any patch with a different/absent key, or an
     * undo/redo, ends the run.
     */
    coalesceKey?: string;
}

export interface TreeState {
    tree: SkillData;
    isDraft: boolean;
    error?: string;
    /** True when load failed because the tree is private or the user lacks access (not a generic load error). */
    privateAccessDenied?: boolean;
    isLoading?: boolean;
    entitlementLevel: EntitlementLevel;
    canEdit: boolean;
    canUndo: boolean;
    canRedo: boolean;
    /**
     * Set when a publish or draft auto-save failed to reach the server, so the local
     * tree has diverged from it; cleared by the next save that succeeds. Distinct from
     * `error`, which the provider treats as a fatal load failure.
     */
    persistenceError?: string;
}

export class TreeInteractor {
    private data: SkillData;
    private updateCallback?: (state: TreeState) => void;
    private persistor: TreeDataPersistor;
    private isDevMode: boolean;
    public isDraft: boolean;
    private treeId: string;
    private isDataLoaded: boolean = false;
    private entitlementLevel: EntitlementLevel = Entitlement.None;
    private canEdit: boolean = false;
    /** Session-scoped undo/redo history over immer patches (in-memory only). */
    private history: UndoRedoInteractor;
    /** See {@link TreeState.persistenceError}. */
    private persistenceError?: string;
    /**
     * What failed. A successful draft auto-save clears only a 'draft' error — a failed
     * publish stays visible until a publish succeeds, since recovered auto-saves don't
     * make the published tree any less stale.
     */
    private persistenceErrorSource?: 'publish' | 'draft';
    /** Orders overlapping draft saves so a stale completion can't overwrite a newer one's outcome. */
    private draftSaveSequence = 0;
    /** Pending trailing-debounce timer for the next draft auto-save. */
    private draftSaveTimer: ReturnType<typeof setTimeout> | null = null;
    /**
     * The published tree as of load time, kept fresh after each successful tree
     * publish and single-guide publish. A read-only UI hint: "is this node in the
     * published tree?" checks are synchronous lookups here (never an edge-function
     * call from menu-rendering code). Server-side validation remains authoritative —
     * concurrent editors can make this stale. Null when the tree was never published.
     */
    private publishedSnapshot: SkillData | null = null;

    constructor(
        data: SkillData,
        persistor: TreeDataPersistor,
        treeId: string,
        isDevMode: boolean = false,
        history: UndoRedoInteractor = new UndoRedoInteractor()
    ) {
        this.data = produce(data, draft => draft);
        this.persistor = persistor;
        this.treeId = treeId;
        this.isDevMode = isDevMode;
        this.isDraft = false; // Start not in draft mode
        this.history = history;
    }

    getData(): SkillData {
        return produce(this.data, draft => draft);
    }

    getIsDataLoaded(): boolean {
        return this.isDataLoaded;
    }

    getTreeId(): string {
        return this.treeId;
    }

    getSelectedNodeId(): string | null {
        return this.data.selectedNodeId || null;
    }

    /** See {@link publishedSnapshot}. */
    getPublishedSnapshot(): SkillData | null {
        return this.publishedSnapshot;
    }

    /** Synchronous {@link publishedSnapshot} lookup — safe to call from render code. */
    isNodeInPublishedTree(nodeId: string): boolean {
        return this.publishedSnapshot?.nodes.some(n => n.id === nodeId) ?? false;
    }

    getSelectedNode(): SkillNode | null {
        if (!this.data.selectedNodeId) return null;
        return this.data.nodes.find(n => n.id === this.data.selectedNodeId) || null;
    }

    setUpdateCallback(callback: (state: TreeState) => void): void {
        this.updateCallback = callback;
    }

    setSelectedNode(nodeId: string | null): void {
        console.log('setSelectedNode', nodeId);
        // Selection is UI state, not a document edit — never an undo step.
        this.patch(draft => {
            draft.selectedNodeId = nodeId;
        }, { undoable: false });
    }

    addNode(name: string, viewport: Viewport, windowWidth: number, windowHeight: number): { node: SkillNode; document: SkillDocument } {
        if (!this.isDevMode) {
            throw new Error('Adding nodes is only allowed in development mode');
        }
        
        const id = this.generateUUID();
        
        // Calculate center of current viewport
        const centerX = -viewport.x / viewport.k + windowWidth / (2 * viewport.k);
        const centerY = -viewport.y / viewport.k + windowHeight / (2 * viewport.k);
        
        // Add some random offset from center
        const offsetX = (Math.random() - 0.5) * 200;
        const offsetY = (Math.random() - 0.5) * 200;
        
        const node: SkillNode = {
            id,
            name,
            fx: centerX + offsetX,
            fy: centerY + offsetY
        };

        const document: SkillDocument = {
            nodeId: id,
            documentPath: null,
        };

        this.patch(draft => {
            draft.nodes.push(node);
            draft.documents.push(document);
        });

        return { node, document };
    }

    /**
     * Links the leaf to a markdown guide (default storage key is the node id) and persists
     * {@link NEW_SKILL_LEAF_GUIDE_STARTER_MARKDOWN} as a draft guide. New leaves from
     * {@link addNode} start without a linked guide.
     */
    async attachGuideToLeaf(nodeId: string): Promise<Result<void, PersistorErrorType>> {
        if (!this.isDevMode) {
            throw new Error('Attaching guides is only allowed in development mode');
        }
        if (!this.isDataLoaded) {
            return fail('Cannot attach guide: data is still loading', 'validation');
        }

        const node = this.data.nodes.find(n => n.id === nodeId);
        if (!node) {
            return ok(undefined);
        }

        const existing = this.data.documents.find(d => d.nodeId === nodeId);
        if (existing && documentEntryHasLinkedGuide(existing)) {
            return ok(undefined);
        }

        const markdownContent = NEW_SKILL_LEAF_GUIDE_STARTER_MARKDOWN;

        // Persist storage first, then link `documentPath` locally. If we patch first,
        // SkillPanelInteractor sees a linked guide and loadDocument races save-document → 400.
        try {
            await this.persistor.saveDocument(nodeId, markdownContent, this.treeId);
        } catch (error) {
            console.error('Failed to save document:', error);
            return fail('Failed to save document', 'system');
        }

        // Undoable like any draft edit: undoing merely orphans the draft starter
        // file, which tree-publish reconciliation cleans up.
        this.patch((draft) => {
            const idx = draft.documents.findIndex((d) => d.nodeId === nodeId);
            if (idx === -1) {
                draft.documents.push({ nodeId, documentPath: nodeId });
            } else {
                draft.documents[idx].documentPath = nodeId;
            }
        });

        return ok(undefined);
    }

    /**
     * Unlinks the leaf's guide in the draft tree (clears {@link SkillDocument.documentPath}).
     * Purely declarative: no storage is touched — draft and published guide files stay
     * in storage until tree publish reconciles them — so this is undoable like any
     * other draft edit.
     */
    async deleteGuideFromLeaf(nodeId: string): Promise<Result<void, PersistorErrorType>> {
        if (!this.isDevMode) {
            throw new Error('Deleting guides is only allowed in development mode');
        }
        if (!this.isDataLoaded) {
            return fail('Cannot delete guide: data is still loading', 'validation');
        }

        const node = this.data.nodes.find((n) => n.id === nodeId);
        if (!node) {
            return ok(undefined);
        }

        const entry = this.data.documents.find((d) => d.nodeId === nodeId);
        if (!entry || !documentEntryHasLinkedGuide(entry)) {
            return ok(undefined);
        }

        this.patch((draft) => {
            const idx = draft.documents.findIndex((d) => d.nodeId === nodeId);
            if (idx >= 0) {
                draft.documents[idx].documentPath = null;
            } else {
                draft.documents.push({ nodeId, documentPath: null });
            }
        });

        return ok(undefined);
    }

    /**
     * Publishes the leaf's draft guide to the published tree without publishing
     * tree-structure changes. Guarded by {@link publishedSnapshot} (a node absent from
     * the published tree cannot have its guide published); the edge function
     * re-validates server-side since the snapshot may be stale.
     */
    async publishGuide(nodeId: string): Promise<Result<void, PersistorErrorType>> {
        if (!this.isDevMode) {
            throw new Error('Publishing guides is only allowed in development mode');
        }
        if (!this.isDataLoaded) {
            return fail('Cannot publish guide: data is still loading', 'validation');
        }
        if (!this.isNodeInPublishedTree(nodeId)) {
            return fail('This skill leaf is not part of the published tree yet. Publish the tree instead.', 'validation');
        }

        const result = await this.persistor.publishGuide(nodeId, this.treeId);
        if (!result.success) {
            return result;
        }

        // Keep the snapshot fresh: the published tree now links this node's guide.
        if (this.publishedSnapshot) {
            this.publishedSnapshot = produce(this.publishedSnapshot, draft => {
                const idx = draft.documents.findIndex(d => d.nodeId === nodeId);
                if (idx === -1) {
                    draft.documents.push({ nodeId, documentPath: nodeId });
                } else if (!documentEntryHasLinkedGuide(draft.documents[idx])) {
                    draft.documents[idx].documentPath = nodeId;
                }
            });
        }

        return ok(undefined);
    }

    renameNode(nodeId: string, newName: string): void {
        if (!this.isDevMode) {
            throw new Error('Renaming nodes is only allowed in development mode');
        }
        
        const nodeIndex = this.data.nodes.findIndex(n => n.id === nodeId);
        if (nodeIndex === -1) return;

        // The rename field applies while typing (debounced), so one editing session can
        // land several renames; coalesce them so the whole burst is a single undo step.
        this.patch(draft => {
            draft.nodes[nodeIndex].name = newName;
        }, { coalesceKey: `rename:${nodeId}` });
    }

    updateTreeName(newName: string): void {
        if (!this.isDevMode) {
            throw new Error('Updating tree name is only allowed in development mode');
        }
        
        this.patch(draft => {
            draft.name = newName;
        });
    }

    updateNodePosition(nodeId: string, x: number, y: number): void {
        if (!this.isDevMode) {
            throw new Error('Updating node positions is only allowed in development mode');
        }
        
        const nodeIndex = this.data.nodes.findIndex(n => n.id === nodeId);
        if (nodeIndex === -1) return;

        // Fired once per completed drag gesture (SkillTree wires this to onNodeDragStop),
        // so each call is already exactly one undo step. If a caller ever streams
        // positions mid-drag, pass `coalesceKey: 'move:<nodeId>'` from that caller so a
        // gesture stays a single entry — don't add it here, or consecutive drags of the
        // same node would merge into one step.
        this.patch(draft => {
            draft.nodes[nodeIndex].fx = x;
            draft.nodes[nodeIndex].fy = y;
        });
    }

    deleteNode(nodeId: string): void {
        if (!this.isDevMode) {
            throw new Error('Deleting nodes is only allowed in development mode');
        }
        
        const nodeIndex = this.data.nodes.findIndex(n => n.id === nodeId);
        if (nodeIndex === -1) return;

        this.patch(draft => {
            draft.nodes = draft.nodes.filter(n => n.id !== nodeId);
            draft.links = draft.links.filter(
                link => link.source !== nodeId && link.target !== nodeId
            );
            draft.documents = draft.documents.filter(doc => doc.nodeId !== nodeId);
            if (draft.selectedNodeId === nodeId) {
                draft.selectedNodeId = null;
            }
        });
    }

    addLink(sourceId: string, targetId: string): void {
        if (!this.isDevMode) {
            throw new Error('Adding links is only allowed in development mode');
        }
        
        // Check if both nodes exist
        const sourceExists = this.data.nodes.some(n => n.id === sourceId);
        const targetExists = this.data.nodes.some(n => n.id === targetId);
        
        if (!sourceExists || !targetExists) return;

        // Check if link already exists
        const linkExists = this.data.links.some(
            link => link.source === sourceId && link.target === targetId
        );

        if (linkExists) return;

        const link: SkillLink = {
            source: sourceId,
            target: targetId
        };

        this.patch(draft => {
            draft.links.push(link);
        });
    }

    deleteLink(sourceId: string, targetId: string): void {
        if (!this.isDevMode) {
            throw new Error('Deleting links is only allowed in development mode');
        }
        
        const linkIndex = this.data.links.findIndex(
            link => link.source === sourceId && link.target === targetId
        );

        if (linkIndex === -1) return;

        this.patch(draft => {
            draft.links = draft.links.filter(
                link => !(link.source === sourceId && link.target === targetId)
            );
        });
    }


    updateViewport(viewport: Viewport): void {
        this.patchWithoutSaving(draft => {
            draft.viewport = { ...viewport };
        });
    }

    setInitialViewport(viewport: Viewport): void {
        // Viewport writes are UI/camera state, not document edits — not an undo step.
        this.patch(draft => {
            draft.initialViewport = { ...viewport };
        }, { undoable: false });
    }

    updateData(data: SkillData): void {
        this.patch(draft => {
            Object.assign(draft, data);
        });
    }

    setDataWithoutSaving(data: SkillData, entitlementLevel: EntitlementLevel = Entitlement.None, canEdit: boolean = false): void {
        this.data = produce(data, draft => draft);
        // Loaded data starts a new document lineage; recorded patches no longer apply.
        this.resetHistory();
        this.isDraft = false; // Loading data doesn't make it a draft
        this.entitlementLevel = entitlementLevel;
        this.canEdit = canEdit;
        this.notify();
        // Don't call saveData() - this is for loading data without persistence
    }

    async saveData(): Promise<void> {
        if (!this.isDataLoaded) {
            throw new Error('Cannot save: data is still loading');
        }
        const published = this.data;
        try {
            await this.persistor.saveData(this.getData(), this.treeId);
        } catch (error) {
            this.persistenceError = 'Publish failed — your changes were not published. Please try again.';
            this.persistenceErrorSource = 'publish';
            this.notify();
            throw error;
        }
        this.persistenceError = undefined;
        this.persistenceErrorSource = undefined;
        // The server now holds exactly what we sent — refresh the published snapshot.
        this.publishedSnapshot = published;
        // A mutation that landed while the request was in flight (e.g. an undo via the
        // global shortcut) means the local tree no longer matches what was published;
        // stay in draft mode so the divergence remains publishable.
        if (this.data === published) {
            this.isDraft = false; // Exit draft mode after saving to main tree
        }
        this.notify();
    }

    private async saveDraft(): Promise<void> {
        await this.persistor.saveDraft(this.getData(), this.treeId);
    }

    async loadData(): Promise<Result<SkillDataState, PersistorErrorType>> {
        const result = await this.persistor.loadData(this.treeId);
        if (!result.success) return result;
        
        this.setDataWithoutSaving(result.data.treeData, result.data.entitlementLevel, result.data.canEdit);
        return result;
    }

    async loadDraft(): Promise<Result<SkillDataState, PersistorErrorType>> {
        const result = await this.persistor.loadDraft(this.treeId);
        if (!result.success) return result;
        
        this.setDataWithoutSaving(result.data.treeData, result.data.entitlementLevel, result.data.canEdit);
        return result;
    }

    async loadInitialData(): Promise<Result<SkillDataState, PersistorErrorType>> {
        let stateResult: Result<SkillDataState, PersistorErrorType>;
        let isDraft = false;
        let publishedTree: SkillData | null = null;

        if (this.isDevMode) {
            // Single editor entry point: working data (draft when one exists) plus the
            // published snapshot in one round trip.
            const initialResult = await this.persistor.loadInitialDevelopmentTree(this.treeId);
            if (!initialResult.success) return initialResult;
            isDraft = initialResult.data.isDraft;
            publishedTree = initialResult.data.publishedTree;
            stateResult = initialResult;
        } else {
            stateResult = await this.loadData();
            // Outside dev mode the loaded tree IS the published tree.
            if (stateResult.success) {
                publishedTree = stateResult.data.treeData;
            }
        }

        if (!stateResult.success) return stateResult;

        const state = stateResult.data;
        console.log('data.version', state.treeData.version);
        console.log('SKILL_DATA_VERSION', SKILL_DATA_VERSION);
        if (state.treeData.version > SKILL_DATA_VERSION) {
            const errorMessage = 'There is an update to the client available. Please refresh the page to get the latest version.';
            this.setErrorState(errorMessage);
            throw new Error(errorMessage);
        }

        this.setDataWithoutSaving(state.treeData, state.entitlementLevel, state.canEdit);
        this.publishedSnapshot = publishedTree;
        this.isDraft = isDraft;
        this.isDataLoaded = true;

        this.notify();

        return ok(state);
    }

    async loadDocument(nodeId: string): Promise<Result<LoadedDocument, PersistorErrorType>> {
        // Editors get the draft guide copy when one exists; everyone else reads the
        // published copy only.
        if (this.isDevMode && this.canEdit) {
            return await this.persistor.loadDocument(nodeId, this.treeId, { preferDraft: true });
        }
        return await this.persistor.loadDocument(nodeId, this.treeId);
    }

    async saveDocument(nodeId: string, content: string): Promise<void> {
        if (!this.isDataLoaded) {
            throw new Error('Cannot save: data is still loading');
        }
        return await this.persistor.saveDocument(nodeId, content, this.treeId);
    }


    get canUndo(): boolean {
        return this.history.canUndo;
    }

    get canRedo(): boolean {
        return this.history.canRedo;
    }

    /** Reverses the most recent undoable mutation and moves it to the redo stack. */
    undo(): void {
        if (!this.isDevMode || !this.isDataLoaded) return;
        const patches = this.history.undo();
        if (!patches) return;
        this.data = applyPatches(this.data, patches);
        this.commitMutation();
    }

    /** Replays the most recently undone mutation and moves it back to the undo stack. */
    redo(): void {
        if (!this.isDevMode || !this.isDataLoaded) return;
        const patches = this.history.redo();
        if (!patches) return;
        this.data = applyPatches(this.data, patches);
        this.commitMutation();
    }

    private patch(recipe: (draft: SkillData) => void, options: PatchOptions = {}): void {
        if (!this.isDataLoaded) {
            // Don't allow modifications while data is loading
            return;
        }
        if (options.undoable === false) {
            // Nothing is recorded, so skip the patch-generation machinery entirely
            // (selection changes ride this path on every node click).
            this.data = produce(this.data, recipe);
            // A non-undoable mutation still ends any in-progress coalescing run.
            this.history.endCoalescing();
        } else {
            const [next, redoPatches, undoPatches] = produceWithPatches(this.data, recipe);
            this.data = next;
            // No-op recipes produce no patches; recording them would add undo steps that do nothing.
            if (redoPatches.length > 0) {
                this.history.record({ undo: undoPatches, redo: redoPatches, coalesceKey: options.coalesceKey });
            }
        }
        this.commitMutation();
    }

    /** Post-mutation path shared by patch/undo/redo: draft flagging, UI notify, draft auto-save. */
    private commitMutation(): void {
        // Draft flag is for dev edit sessions (persisted tree draft). Viewers may call `patch`
        // only for ephemeral state like `selectedNodeId`; that must not show as a tree draft.
        if (this.isDevMode) {
            this.isDraft = true;
        }
        this.notify();
        // Only auto-save data changes in dev mode
        if (this.isDevMode) {
            this.scheduleDraftSave();
        }
    }

    /**
     * Trailing-debounced draft auto-save: each mutation restarts the timer, so a burst
     * saves once with its final state. saveDraft() serializes the live tree at send
     * time, so the coalesced save always carries the latest data.
     */
    private scheduleDraftSave(): void {
        if (this.draftSaveTimer !== null) {
            clearTimeout(this.draftSaveTimer);
        }
        this.draftSaveTimer = setTimeout(() => {
            this.draftSaveTimer = null;
            void this.performDraftSave();
        }, DRAFT_SAVE_DEBOUNCE_MS);
    }

    /**
     * Runs any pending debounced draft save immediately. Safe to call when nothing is
     * pending; never rejects (failures surface via {@link TreeState.persistenceError}).
     */
    async flushPendingDraftSave(): Promise<void> {
        if (this.draftSaveTimer === null) return;
        clearTimeout(this.draftSaveTimer);
        this.draftSaveTimer = null;
        await this.performDraftSave();
    }

    private async performDraftSave(): Promise<void> {
        const sequence = ++this.draftSaveSequence;
        try {
            await this.saveDraft();
            if (sequence !== this.draftSaveSequence) return; // a newer save owns the outcome
            if (this.persistenceErrorSource === 'draft') {
                this.persistenceError = undefined;
                this.persistenceErrorSource = undefined;
                this.notify();
            }
        } catch (error) {
            console.error('Failed to save draft:', error);
            if (sequence !== this.draftSaveSequence) return;
            this.persistenceError = 'Changes could not be saved to the server — they will retry on your next edit.';
            this.persistenceErrorSource = 'draft';
            this.notify();
        }
    }

    /**
     * Drops both history stacks. Patches are only valid against the document lineage they
     * were recorded on, so every code path that replaces `this.data` without going through
     * `patch()` (initial load, draft load, tree switch, version migrations) must call this.
     */
    private resetHistory(): void {
        this.history.reset();
    }

    private patchWithoutSaving(recipe: (draft: SkillData) => void): void {
        // Viewport-only writes: same document lineage (plain produce), never recorded on
        // the undo stack and no history reset — recorded patches stay valid across them.
        this.data = produce(this.data, recipe);
        // Don't mark as draft or save for viewport changes
        this.notify();
    }

    private setErrorState(error: string): void {
        this.notify(error);
    }

    private notify(error?: string): void {
        if (!this.updateCallback) return;
        this.updateCallback({
            tree: this.getData(),
            isDraft: this.isDraft,
            error,
            entitlementLevel: this.entitlementLevel,
            canEdit: this.canEdit,
            canUndo: this.canUndo,
            canRedo: this.canRedo,
            persistenceError: this.persistenceError
        });
    }

    private generateUUID(): string {
        return crypto.randomUUID();
    }

}
